import {makeAutoObservable, observable, runInAction} from 'mobx'
import React from 'react'
import {Lang} from 'yougile-lang'
import {
  FieldError,
  FormErrors,
  FormState,
  FormUtil,
  ObjUtil,
  Util
} from 'yougile-util'
import {
  EmailChangeReq,
  EmailChangeResp,
  EmailSettingsKeyResp,
  GoogleCalendarIntegrationsStatus,
  GoogleCalendarOption,
  GoogleCalendarSettings,
  PassChangeReq,
  PassChangeResp,
  User
} from 'yougile-model'
import {Accounts} from '@client/accounts'
import {Sync} from '@client/sync'
import {Backend} from '@common/backend'
import {FileChooser} from '@common/file-chooser'
import {
  getGoogleCalendarSettingsData,
  googleCalendarSignIn,
  setGoogleCalendarStatus
} from '@common/google-calendar'
import {
  getSelectedBoards,
  GoogleCalendarFormData
} from '@components/account/profile/google-calendar-settings-popup/use-google-calendar-settings-form'
import {InputApi} from '@components/common/base/input/types'
import {MessageBox} from '@components/message-box'
import {createUseStore} from '@containers/hooks'
import {globals} from '@extensions/globals-base'

export interface ChangeEmailFormFields {
  currentPassword: string
  newEmail: string
}

const changeEmailFormInitialState: ChangeEmailFormFields = {
  currentPassword: '',
  newEmail: ''
}
interface GoogleCalendarIntegrationData {
  calendarOptions?: GoogleCalendarOption[]
  userEmail?: string
  settings?: GoogleCalendarSettings
}

const googleCalendarFormInitialState: GoogleCalendarFormData = {
  email: '',
  calendar: '',
  boards: [],
  tasksType: 'my'
}

interface State {
  realName: string
  oldRealName: string
  error: string

  mailLink: string
  mailLinkEmail: string
  mailLinkWait: boolean

  changePassShow: boolean
  changePassNewPassword: string
  changePassNewAgainPassword: string
  changePassOldPassword: string
  changePassError: string
  changePassWait: boolean

  changeEmailPopupShow: boolean
  changeEmailPopupState: 'form' | 'request-created'
  changeEmailWait: boolean
  changeEmailForm: FormState<ChangeEmailFormFields>

  googleIntegrationPopupShow: boolean
  googleCalendarSettingsStatus: GoogleCalendarIntegrationsStatus | undefined
  googleCalendarIntegrationData: GoogleCalendarIntegrationData | undefined
  googleCalendarForm: FormState<GoogleCalendarFormData>
  googleCalendarInitValues: GoogleCalendarFormData | undefined
  googleCalendarFormChanged: boolean
  waitGoogleCalendar: boolean
}

export class ProfileStore {
  state: State = {
    realName: '',
    oldRealName: '',
    error: '',

    mailLink: '',
    mailLinkEmail: '',
    mailLinkWait: false,

    changePassShow: false,
    changePassNewPassword: '',
    changePassNewAgainPassword: '',
    changePassOldPassword: '',
    changePassError: '',
    changePassWait: false,

    changeEmailPopupShow: false,
    changeEmailPopupState: 'form',
    changeEmailWait: false,
    changeEmailForm: FormUtil.initForm(changeEmailFormInitialState),

    googleIntegrationPopupShow: false,
    googleCalendarSettingsStatus: undefined,
    googleCalendarIntegrationData: undefined,
    googleCalendarForm: FormUtil.initForm(googleCalendarFormInitialState),
    googleCalendarInitValues: undefined,
    googleCalendarFormChanged: false,
    waitGoogleCalendar: false
  }

  constructor() {
    makeAutoObservable(
      this,
      {
        state: observable.shallow
      },
      {autoBind: true}
    )
  }

  async update(): Promise<void> {
    const realName = Accounts.getName(globals.currentUserId)

    this.state.realName = realName
    this.state.oldRealName = realName
    this.state.error = ''

    const user = Sync.getCurrentUser()
    const companyUser = Sync.getCurrentSandbox().get(user.id) as User

    const companyId = globals.getCompanyId()

    if (!globals.standalone && !Util.isDesktop()) {
      const userGCSettingsStatus =
        companyUser?.integrations?.googleCalendar?.[companyId]?.status

      if (userGCSettingsStatus) {
        this.state.waitGoogleCalendar = false

        if (userGCSettingsStatus === 'error') {
          MessageBox.show(
            Lang.msg.googleCalendar.synchronizationIsNotSet,
            'warning'
          )
          await setGoogleCalendarStatus('off')
        }
        if (userGCSettingsStatus === 'isReady') {
          await this.openGoogleSettingsPopup()
        }
        if (userGCSettingsStatus === 'off') {
          this.closeGoogleSettingsPopup()
        }
      }
    }

    await this.updateMailLink()
  }

  private async updateMailLink(): Promise<void> {
    const user = Sync.getCurrentUser()

    // update link only if first render or user email has been changed
    if (this.state.mailLinkEmail === user.email) {
      return
    }

    runInAction(() => {
      this.state.mailLinkWait = true
    })

    try {
      const {key} = await Backend.postJSON<EmailSettingsKeyResp>(
        '/data/mail-settings/generate-key',
        Backend.loadCred()
      )
      const mailLink = Backend.toMainUrl(
        `${globals.standalone ? '/team' : ''}/mail-settings?email=` +
          encodeURIComponent(user.email) +
          `&key=${key}&lang=${Lang.getLang()}`
      )

      runInAction(() => {
        this.state.mailLink = mailLink
        this.state.mailLinkEmail = user.email
      })
    } catch (e) {
      const error = Util.extractErrorMessage(e)
      MessageBox.show(Lang.tryLocalize(error), 'danger')
    } finally {
      runInAction(() => {
        this.state.mailLinkWait = false
      })
    }
  }

  setIfGCFormIsChanged(): void {
    if (
      this.state.googleCalendarInitValues &&
      this.state.googleCalendarForm.data
    ) {
      this.state.googleCalendarFormChanged = ObjUtil.keys(
        this.state.googleCalendarForm.data
      ).some(formKey => {
        const prevData = this.state?.googleCalendarInitValues?.[formKey]
        const data = this.state.googleCalendarForm.data[formKey]

        if (Array.isArray(prevData) && Array.isArray(data)) {
          if (prevData.length !== data.length) {
            return true
          } else {
            const prevDataSet = new Set(prevData)
            return data.some(boardId => !prevDataSet.has(boardId))
          }
        }

        return prevData !== data
      })
    }
  }

  updateGoogleCalendarSettingsFormField(
    delta: Partial<GoogleCalendarFormData>
  ): void {
    this.state.googleCalendarForm = FormUtil.updateForm(
      this.state.googleCalendarForm,
      delta
    )
    this.setIfGCFormIsChanged()
  }

  validateGCSettingsForm(): FormErrors<GoogleCalendarSettings> | undefined {
    const errors: FieldError[] = []
    const {data: formData} = this.state.googleCalendarForm
    for (const formKey of ObjUtil.keys(formData)) {
      if (
        (Array.isArray(formData[formKey]) && !formData[formKey]?.length) ||
        !formData[formKey]
      ) {
        errors.push({
          field: formKey,
          errors: [Lang.msg.googleCalendar.requiredField]
        })
      }
    }
    if (errors.length) {
      this.state.googleCalendarForm = FormUtil.applyErrors(
        this.state.googleCalendarForm,
        errors
      )

      return this.state.googleCalendarForm.errors
    }
    return undefined
  }

  setGoogleSettingsInitValues(): void {
    const {userEmail, settings, calendarOptions} =
      this.state.googleCalendarIntegrationData || {}

    this.state.googleCalendarInitValues = {...settings, email: userEmail}

    const calendar =
      settings?.calendar &&
      calendarOptions?.find(option => option.id === settings?.calendar)
        ? settings.calendar
        : undefined

    this.state.googleCalendarForm = FormUtil.initForm({
      calendar:
        calendar ??
        (calendarOptions && calendarOptions?.length === 1
          ? calendarOptions[0].id
          : undefined),
      email: userEmail,
      boards: settings?.companiesBoards
        ? getSelectedBoards(settings.companiesBoards)
        : [],
      tasksType: settings?.tasksType || 'my'
    })
  }

  async startGCIntegration(): Promise<void> {
    this.state.waitGoogleCalendar = true
    await googleCalendarSignIn()
  }

  async syncGoogleCalendarSettingsData(): Promise<void> {
    if (globals.standalone || Util.isDesktop()) {
      return
    }
    this.state.waitGoogleCalendar = true

    const googleCalendarResponse = await getGoogleCalendarSettingsData(
      globals.getCompanyId()
    )

    if (!googleCalendarResponse) {
      this.state.googleCalendarSettingsStatus = 'error'
      this.state.waitGoogleCalendar = false

      return
    }

    const {status, ...googleCalendar} = googleCalendarResponse

    this.state.googleCalendarIntegrationData = {
      calendarOptions: googleCalendar.calendarOptions?.map(calendarOption => {
        return {
          ...calendarOption,
          name: calendarOption.primary
            ? Lang.msg.googleCalendar.primaryCalendar
            : calendarOption.name
        }
      }),
      userEmail: googleCalendar.userEmail,
      settings: googleCalendar.settings
    }
    this.state.googleCalendarSettingsStatus = status

    this.state.waitGoogleCalendar = false
  }

  async openGoogleSettingsPopup(): Promise<void> {
    await this.syncGoogleCalendarSettingsData()

    if (this.state.googleCalendarSettingsStatus !== 'error') {
      this.setGoogleSettingsInitValues()
      this.state.googleIntegrationPopupShow = true
    }
  }

  closeGoogleSettingsPopup(): boolean {
    this.state.googleIntegrationPopupShow = false
    return false
  }

  changePassShow(value: boolean): void {
    this.state.changePassShow = value
  }

  changePassOld(value: string): void {
    this.state.changePassError = ''
    this.state.changePassOldPassword = value
  }

  changePassNew(value: string): void {
    this.state.changePassError = ''
    this.state.changePassNewPassword = value
  }

  changePassNewAgain(value: string): void {
    this.state.changePassError = ''
    this.state.changePassNewAgainPassword = value
  }

  submitChangePass = async (): Promise<void> => {
    if (this.state.changePassWait) {
      return
    }
    if (!this.state.changePassNewPassword) {
      this.state.changePassError = Lang.msg.newPassEmpty
      return
    }
    if (this.state.changePassNewPassword.length < 8) {
      this.state.changePassError = Lang.msg.profilePage.newPasswordTooShort
      return
    }
    if (
      this.state.changePassNewPassword !== this.state.changePassNewAgainPassword
    ) {
      this.state.changePassError = Lang.msg.profilePage.newPasswordNotMatch
      return
    }
    this.state.changePassWait = true
    const req: PassChangeReq = {
      uid: globals.currentUserId,
      oldPassword: this.state.changePassOldPassword,
      newPassword: this.state.changePassNewPassword
    }
    try {
      await Backend.postJSON<PassChangeResp>('/data/pass-change', req)
      this.state.changePassError = ''
      this.state.changePassNewPassword = ''
      this.state.changePassOldPassword = ''
      this.state.changePassShow = false
      this.state.changePassWait = false
      MessageBox.show(Lang.msg.passChangeOk, 'success')
    } catch (e) {
      this.state.changePassError = Lang.msg.incorrectOldPass
      this.state.changePassWait = false
    }
  }

  openChangeEmailPopup(): void {
    this.state.changeEmailForm = FormUtil.initForm(changeEmailFormInitialState)
    this.state.changeEmailPopupState = 'form'
    this.state.changeEmailPopupShow = true
  }

  closeChangeEmailPopup(): boolean {
    this.state.changeEmailPopupShow = false
    return true
  }

  updateChangeEmailForm(delta: Partial<ChangeEmailFormFields>): void {
    this.state.changeEmailForm = FormUtil.updateForm(
      this.state.changeEmailForm,
      delta
    )
  }

  submitChangeEmailForm = async (): Promise<void> => {
    if (this.state.changeEmailWait) {
      return
    }

    const req: EmailChangeReq = {
      uid: globals.currentUserId,
      ...FormUtil.extractData(this.state.changeEmailForm, {
        newEmail: x => x.trim(),
        currentPassword: x => x.trim()
      })
    }

    this.state.changeEmailWait = true
    try {
      const res = await Backend.postJSON<EmailChangeResp>(
        '/data/email-change',
        req,
        {rawErrors: true}
      )

      if (res.result === 'ok') {
        this.state.changeEmailPopupState = 'request-created'
      } else if (res.errors) {
        this.state.changeEmailForm = FormUtil.applyErrors(
          this.state.changeEmailForm,
          res.errors
        )
      } else {
        const error = res.error ?? 'errorUnknown'
        let message = ''
        // todo: remove this `if` when localizable error class is introduced
        if (error.startsWith('errorForbiddenByCompanyPermissions')) {
          const params = error.split(':') as [string, string]
          message = Lang.msg.errorForbiddenByCompanyPermissions(params[1])
        } else {
          message = Lang.tryLocalize(error)
        }
        MessageBox.show(message, 'danger')
      }
    } catch (e) {
      const error = Util.extractErrorMessage(e)
      MessageBox.show(Lang.tryLocalize(error), 'danger')
    } finally {
      this.state.changeEmailWait = false
    }
  }

  uploadPhoto(e: React.MouseEvent): void {
    const account = Accounts.get(globals.currentUserId)
    void FileChooser.uploadImage(e).then(({url}) => {
      account.avatar = url
      void Accounts.update(account)
    })
  }

  deletePhoto(): void {
    const account = Accounts.get(globals.currentUserId)
    account.avatar = ''
    void Accounts.update(account)
  }

  changeRealName(api: InputApi): void {
    this.clearError()
    this.state.realName = api.text
  }

  saveSettings(): void {
    const realName = this.state.realName.trim()
    if (!realName) {
      this.state.error = Lang.msg.nameEmpty
      return
    }
    if (realName.length < 3) {
      this.state.error = Lang.msg.nameShort
      return
    }
    const account = Accounts.get(globals.currentUserId)
    if (account.realName !== realName) {
      this.state.oldRealName = realName
      account.realName = realName
      void Accounts.update(account)
    }
    this.state.realName = realName
  }

  revertSettings(api?: InputApi): void {
    this.state.realName = Accounts.getName(globals.currentUserId)
    api?.baseElement?.blur()
  }

  clearError(): void {
    this.state.error = ''
  }
}

const [ProfileContext, useProfileStore] = createUseStore(ProfileStore)
export {ProfileContext, useProfileStore}
